import React, { useState, useMemo, useEffect } from 'react';
import { DataGridPro, GRID_CHECKBOX_SELECTION_COL_DEF, useGridApiContext, useGridApiRef } from '@mui/x-data-grid-pro';
import { QuickFilter, QuickFilterControl, QuickFilterClear, getGridStringOperators, Toolbar } from '@mui/x-data-grid';
import { Alert, TextField, IconButton, Divider, InputAdornment, ListItemText, Stack, ClickAwayListener, Button, Typography, Menu, Box, MenuItem, FormControl, Select, Fade, Popper, Paper, MenuList, InputLabel, Badge, Dialog, DialogTitle, ListItemIcon, DialogContentText, DialogContent, DialogActions } from '@mui/material';
import { DensitySmall, DensityMedium, DensityLarge, ViewColumn, OpenInNew, Clear, Add, Delete, KeyboardArrowDown, KeyboardArrowUp, FilterList, Search, Cancel } from '@mui/icons-material';
import { ThemeProvider, createTheme, styled, useTheme } from '@mui/material/styles';
import { colours } from '../theme/constants';
import useLocalStorage from '../hooks/useLocalStorage';
import ChooseColumnsDialog from '../contacts/dialogs/ChooseColumnsDialog';
import FilteredGroupsSelector from '../contacts/FilteredGroupsSelector';

const DensitySelector = ({ density, onDensityChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClose = value => {
        if (value !== '') {
            onDensityChange(value);
        }

        setAnchorEl(null);
    };

    const startIcon = density === 'compact'
        ? <DensitySmall />
        : density === 'standard'
            ? <DensityMedium />
            : <DensityLarge />;

    return (
        <>
            <Button
                id="density-button"
                aria-controls={open ? 'density-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={e => setAnchorEl(e.currentTarget)}
                variant="text"
                startIcon={startIcon}
            >
                Density
            </Button>
            <Menu
                id="density-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose('')}
                slotProps={{
                    list: {
                        'aria-labelledby': 'density-button'
                    }
                }}
            >
                <MenuItem value="compact" onClick={() => handleClose('compact')}>
                    <ListItemIcon>
                        <DensitySmall fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Compact</ListItemText>
                </MenuItem>
                <MenuItem value="standard" onClick={() => handleClose('standard')}>
                    <ListItemIcon>
                        <DensityMedium fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Standard</ListItemText>
                </MenuItem>
                <MenuItem value="comfortable" onClick={() => handleClose('comfortable')}>
                    <ListItemIcon>
                        <DensityLarge fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Comfortable</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};

const QuickSearch = () => {
    return (
        <QuickFilter
            expanded
            debounceMs={1000}
        >
            <QuickFilterControl
                render={({ ref, ...other }) => (
                    <TextField
                        {...other}
                        inputRef={ref}
                        aria-label="Search by email..."
                        placeholder="Search by email..."
                        variant="outlined"
                        size="small"
                        margin="none"
                        sx={{ width: 265 }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: other.value ? (
                                    <InputAdornment position="end">
                                        <QuickFilterClear
                                            edge="end"
                                            size="small"
                                            aria-label="Clear search"
                                        >
                                            <Cancel fontSize="small" />
                                        </QuickFilterClear>
                                    </InputAdornment>
                                ) : null,
                                ...other.slotProps?.input
                            },
                            ...other.slotProps
                        }}
                    />
                )}
            />
        </QuickFilter>
    );
};

const ActionsMenu = ({ selectedGroup, selectedPseudoGroup, onExport, onCopy, onMove, onDeleteFromGroup, onDeleteFromMultipleGroups, onDeleteFromAccount, onSuppress, onUnsuppress, onUnsubscribe, selectedContacts, onWarnNoSelection, onResubscribe, status }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = action => {
        selectedContacts ? action() : onWarnNoSelection();
        handleClose();
    };

    return (
        <>
            <Button
                id="actions-menu-button"
                aria-controls={open ? 'actions-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleOpen}
                endIcon={open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            >
                Actions
            </Button>
            <Menu
                id="actions-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    list: {
                        'aria-labelledby': 'actions-menu-button'
                    }
                }}
            >
                <MenuItem onClick={() => handleClick(onCopy)}>Copy to Another Group</MenuItem>
                {selectedGroup && <MenuItem onClick={() => handleClick(onMove)}>Move to Another Group</MenuItem>}
                <MenuItem onClick={() => handleClick(onSuppress)}>Suppress</MenuItem>
                <MenuItem onClick={() => handleClick(onUnsuppress)}>Unsuppress</MenuItem>
                {(status !== 'unsubscribed' && status !== 'bounced') && <MenuItem onClick={() => handleClick(onUnsubscribe)}>Unsubscribe</MenuItem>}
                {(status === 'unsubscribed' || status === 'bounced') && <MenuItem onClick={() => handleClick(onResubscribe)}>Resubscribe</MenuItem>}
                {selectedGroup && <MenuItem onClick={() => handleClick(onDeleteFromGroup)}>Delete from This Group</MenuItem>}
                {(selectedPseudoGroup !== 'ungrouped') && <MenuItem onClick={() => handleClick(onDeleteFromMultipleGroups)}>Delete from Multiple Groups</MenuItem>}
                {(status !== 'unsubscribed' && status !== 'bounced') && <MenuItem onClick={() => handleClick(onDeleteFromAccount)}>Delete from Your Account</MenuItem>}
                <MenuItem onClick={() => handleClick(onExport)}>Export</MenuItem>
            </Menu>
        </>
    );
};

const NewViewListButton = ({ onSubmit, views }) => {
    const [isAddingView, setIsAddingView] = useState(false);
    const [label, setLabel] = useState('');

    const handleSubmitForm = e => {
        e.preventDefault();
        onSubmit(label);
        setIsAddingView(false);
        setLabel('');
    };

    const handleLabelChange = (event) => {
        setLabel(event.target.value);
        event.stopPropagation();
    };

    const isNewViewLabelValid = useMemo(() => {
        if (label.length === 0) {
            return false;
        }

        return Object.values(views).every(
            (view) => view.label.localeCompare(label, 'en-UK', { sensitivity: 'base' }) !== 0
        );
    }, [views, label]);

    return (
        <React.Fragment>
            <Button
                startIcon={<Add />}
                size="medium"
                onClick={() => setIsAddingView(true)}
                variant="text"
            >
                Save current view
            </Button>
            <Dialog onClose={() => setIsAddingView(false)} open={isAddingView}>
                <form onSubmit={handleSubmitForm}>
                    <DialogTitle>New View</DialogTitle>
                    <DialogContent>
                        <Alert sx={{ mb: 1 }} severity="info">
                            Saved views will be available on this device until they are deleted.
                        </Alert>
                        {!isNewViewLabelValid && (
                            <Alert sx={{ mb: 1 }} severity="warning">
                                The view has to have a unique name and can't be empty
                            </Alert>
                        )}
                        <TextField
                            autoFocus
                            value={label}
                            onChange={handleLabelChange}
                            margin="dense"
                            size="small"
                            label="Name"
                            variant="standard"
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" type="button" onClick={() => setIsAddingView(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!isNewViewLabelValid}>
                            Create View
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </React.Fragment>
    );
};

const ViewControls = ({ filters, setFilters, views, setViews, activeViewId, setActiveViewId }) => {
    const apiRef = useGridApiContext();
    const [anchorEl, setAnchorEl] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [viewToDelete, setViewToDelete] = useState(null);
    const theme = useTheme();

    const handleCreateNewView = label => {
        //todo(?) use this to load the most recent saved view by default
        const timestamp = new Date().getTime();

        const { columns, pagination, pinnedColumns, sorting } = apiRef.current.exportState();

        const activeFilters = filters?.filter(e => e.field);

        const value = {
            columns,
            pagination,
            pinnedColumns,
            sorting,
            ...(activeFilters && { filters: activeFilters })
        };

        setViews({
            ...views,
            [timestamp]: { label, value }
        });

        setActiveViewId(timestamp);
    };

    const handleDeleteView = (viewId) => {
        const newViews = Object.fromEntries(
            Object.entries(views).filter(([id]) => id !== viewId)
        );

        let newActiveViewId = activeViewId;

        if (activeViewId === viewId) {
            const viewIds = Object.keys(views);

            if (viewIds.length === 0) {
                newActiveViewId = null;
            }
            else {
                newActiveViewId = viewIds[0];
            }
        }

        setViews(newViews);
        setActiveViewId(newActiveViewId);
    };

    const handleSetActiveView = (viewId) => {
        const viewValue = views[viewId].value;

        apiRef.current.restoreState(viewValue);

        setActiveViewId(viewId);
        setIsMenuOpen(false);

        //if view has filters (with field, ie not just the template), set them as the filters, overriding what was there
        //if no filters, clear filters for consistent behaviour
        const filters = viewValue?.filters?.filter(e => e.field);

        setFilters(filters || null);
    };

    const handlePopperAnchorClick = (event) => {
        event.stopPropagation();
        setIsMenuOpen(!isMenuOpen);
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopper = () => {
        setIsMenuOpen(false);
    };

    const handleListKeyDown = (event) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            handleClosePopper();
        }
        else if (event.key === 'Escape') {
            handleClosePopper();
        }
    };

    const canBeMenuOpened = isMenuOpen && Boolean(anchorEl);
    const popperId = canBeMenuOpened ? 'transition-popper' : undefined;

    return (
        <>
            <Stack direction="row" spacing={1}>
                <Button
                    aria-describedby={popperId}
                    variant="text"
                    type="button"
                    size="medium"
                    id="view-button"
                    aria-controls={isMenuOpen ? 'view-menu' : undefined}
                    aria-expanded={isMenuOpen ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handlePopperAnchorClick}
                    disabled={Object.keys(views).length === 0}
                >
                    Saved Views ({Object.keys(views).length})
                </Button>
                <ClickAwayListener onClickAway={handleClosePopper}>
                    <Popper
                        id={popperId}
                        open={isMenuOpen}
                        anchorEl={anchorEl}
                        role={undefined}
                        transition
                        placement="bottom-start"
                        sx={{ zIndex: theme.zIndex.modal }}
                    >
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={350}>
                                <Paper>
                                    <MenuList
                                        autoFocusItem={isMenuOpen}
                                        id="view-menu"
                                        aria-labelledby="view-button"
                                        onKeyDown={handleListKeyDown}
                                    >
                                        {Object.entries(views).map(([viewId, view]) => (
                                            <MenuItem
                                                key={viewId}
                                                selected={viewId === activeViewId}
                                                onClick={() => handleSetActiveView(viewId)}
                                            >
                                                <ListItemText>{view.label}</ListItemText>
                                                <IconButton
                                                    edge="end"
                                                    aria-label="delete"
                                                    size="small"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        setViewToDelete({ id: viewId, label: view.label });
                                                    }}
                                                    sx={{ marginLeft: 2 }}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </Paper>
                            </Fade>
                        )}
                    </Popper>
                </ClickAwayListener>
                <NewViewListButton
                    views={views}
                    onSubmit={handleCreateNewView}
                />
            </Stack>
            <Dialog onClose={() => setViewToDelete(null)} open={Boolean(viewToDelete)}>
                <DialogTitle>Delete View</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete '{viewToDelete?.label ?? 'this view'}'?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" type="button" onClick={() => setViewToDelete(null)}>
                        Cancel
                    </Button>
                    <Button onClick={() => handleDeleteView(viewToDelete?.id)}>
                        Delete View
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const DataGridTools = ({
    count,
    selectedContacts,
    allContactsSelected,
    onToggleAllContactsSelected,
    selectedGroup,
    selectedPseudoGroup,
    setSelectedSegment,
    onShowFilters,
    filters,
    setFilters,
    status,
    setStatus,
    tab,
    groups,
    selectedSegment,
    allContactsCount,
    onExport,
    onCopy,
    onMove,
    onDeleteFromGroup,
    onDeleteFromMultipleGroups,
    onDeleteFromAccount,
    onSuppress,
    onUnsuppress,
    onUnsubscribe,
    views,
    setViews,
    activeViewId,
    setActiveViewId,
    onWarnNoSelection,
    onResubscribe,
    setShowChooseColumnsDialog,
    onCreateGroup, // TODO use a state hook
    density,
    onDensityChange
}) => {
    const showSelectedTools = selectedContacts.ids?.size > 0 || allContactsSelected;
    const selectedCount = allContactsSelected ? count : selectedContacts.ids?.size ?? 0;
    const [showChooseSegmentGroupDialog, setShowChooseSegmentGroupDialog] = useState(false);
    const segmentGroupName = selectedSegment?.groupId ? groups.find(e => e.id === selectedSegment?.groupId)?.name : 'All Contacts';

    //segments
    if (tab === 1) {
        return (
            <>
                <Toolbar sx={selectedSegment ? { pl: 2, minHeight: 64 } : { justifyContent: 'flex-end', minHeight: 64 }}>
                    {selectedSegment && (
                        <Box sx={{ flexGrow: 1 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Button
                                    endIcon={<OpenInNew />}
                                    onClick={() => setShowChooseSegmentGroupDialog(true)}
                                >
                                    Choose Group: {segmentGroupName}
                                </Button>
                                <Button
                                    onClick={onExport}
                                >
                                    Export
                                </Button>
                            </Stack>
                        </Box>
                    )}
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="text"
                            onClick={() => setShowChooseColumnsDialog(true)}
                            startIcon={<ViewColumn />}
                        >
                            Columns
                        </Button>
                        <DensitySelector
                            density={density}
                            onDensityChange={onDensityChange}
                        />
                        <Button
                            variant="text"
                            onClick={onShowFilters}
                            startIcon={(
                                <Badge badgeContent={filters?.length} color="primary">
                                    <FilterList />
                                </Badge>
                            )}
                            disabled={tab === 1 && !selectedSegment}
                        >
                            Filters
                        </Button>
                    </Stack>
                </Toolbar>
                <Divider />
                <Dialog onClose={() => setShowChooseSegmentGroupDialog(false)} open={showChooseSegmentGroupDialog}>
                    <DialogTitle>Choose Group</DialogTitle>
                    <FilteredGroupsSelector
                        selectedGroups={selectedSegment?.groupId ? [selectedSegment?.groupId] : null}
                        setSelectedGroups={selectedGroups => {
                            const groupId = selectedGroups && selectedGroups.length > 0 ? selectedGroups[0] : null;
                            setSelectedSegment({ ...selectedSegment, groupId });
                        }}
                        spacing={3}
                        popperGutters={3}
                        multipleSelection={false}
                        includeAllContacts={true}
                        allContactsCount={allContactsCount}
                        onCreateGroup={onCreateGroup} // TODO use a hook
                    />
                    <DialogActions>
                        <Button variant="outlined" onClick={() => setShowChooseSegmentGroupDialog(false)}>Cancel</Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    const toolbarStyles = showSelectedTools ? { backgroundColor: '#E0E0E0' } : null;

    return (
        <>
            <Toolbar sx={{ justifyContent: 'flex-start', pl: 2, minHeight: 64, ...toolbarStyles }}>
                {showSelectedTools ? (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ marginRight: 2 }}>
                            {`${selectedCount} contact${selectedCount > 1 ? 's' : ''} selected`}
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={allContactsSelected ? <Clear /> : null}
                            onClick={() => onToggleAllContactsSelected(!allContactsSelected)}
                        >
                            {allContactsSelected ? 'Clear selection' : `Select all ${count} contact${count > 1 ? 's' : ''}`}
                        </Button>
                        <ActionsMenu
                            selectedGroup={selectedGroup}
                            selectedPseudoGroup={selectedPseudoGroup}
                            onExport={onExport}
                            onCopy={onCopy}
                            onMove={onMove}
                            onDeleteFromGroup={onDeleteFromGroup}
                            onDeleteFromMultipleGroups={onDeleteFromMultipleGroups}
                            onDeleteFromAccount={onDeleteFromAccount}
                            onSuppress={onSuppress}
                            onUnsuppress={onUnsuppress}
                            onUnsubscribe={onUnsubscribe}
                            selectedContacts={Boolean(selectedContacts.ids?.size > 0 || allContactsSelected)}
                            onWarnNoSelection={onWarnNoSelection}
                            onResubscribe={onResubscribe}
                            status={status}
                        />
                    </Stack>
                ) : (
                    <>
                        <Box sx={{ flexGrow: 1 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <QuickSearch />
                                <FormControl size="small">
                                    <InputLabel id="status-label">Status</InputLabel>
                                    <Select
                                        labelId="status-label"
                                        id="status"
                                        value={status}
                                        label="Status"
                                        onChange={event => setStatus(event.target.value)}
                                    >
                                        <MenuItem value="subscribed">Subscribers</MenuItem>
                                        <MenuItem value="unsubscribed">Unsubscribers</MenuItem>
                                        <MenuItem value="bounced">Hard Bounces</MenuItem>
                                    </Select>
                                </FormControl>
                                <ActionsMenu
                                    selectedGroup={selectedGroup}
                                    selectedPseudoGroup={selectedPseudoGroup}
                                    onExport={onExport}
                                    onCopy={onCopy}
                                    onMove={onMove}
                                    onDeleteFromGroup={onDeleteFromGroup}
                                    onDeleteFromMultipleGroups={onDeleteFromMultipleGroups}
                                    onDeleteFromAccount={onDeleteFromAccount}
                                    onSuppress={onSuppress}
                                    onUnsuppress={onUnsuppress}
                                    onUnsubscribe={onUnsubscribe}
                                    selectedContacts={Boolean(selectedContacts.ids?.size > 0 || allContactsSelected)}
                                    onWarnNoSelection={onWarnNoSelection}
                                />
                            </Stack>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <ViewControls
                                filters={filters}
                                setFilters={setFilters}
                                views={views}
                                setViews={setViews}
                                activeViewId={activeViewId}
                                setActiveViewId={setActiveViewId}
                            />
                            <Button
                                variant="text"
                                onClick={() => setShowChooseColumnsDialog(true)}
                                startIcon={<ViewColumn />}
                            >
                                Columns
                            </Button>
                            <DensitySelector
                                density={density}
                                onDensityChange={onDensityChange}
                            />
                            <Button
                                variant="text"
                                onClick={onShowFilters}
                                startIcon={(
                                    <Badge badgeContent={filters?.length} color="primary">
                                        <FilterList />
                                    </Badge>
                                )}
                            >
                                Filters
                            </Button>
                        </Stack>
                    </>
                )}
            </Toolbar>
            <Divider />
        </>
    );
};

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-cell--pinnedRight': {
        backgroundColor: colours.primarySelected
    },
    '.MuiDataGrid-columnHeader--pinnedLeft, .MuiDataGrid-columnHeader--pinnedRight': {
        backgroundColor: colours.primarySelected
    },
    '.row-suppressed': {
        color: theme.palette.text.secondary
    }
}));

const getTableHeight = theme => {
    const viewportHeight = window.innerHeight;
    const toolbarHeight = 64; //true if vw > 600. See https://github.com/mui/material-ui/issues/10739 for trouble creating this number
    const themeSpacing = theme.spacing(1).split('px')[0];

    //this is 2 toolbars and table margin. Update if page layout is changed
    return viewportHeight - ((toolbarHeight * 2) + (themeSpacing * 4));
};

const ContactsTable = ({
    data,
    count,
    columns,
    rowsPerPageOptions,
    rowsPerPage,
    setRowsPerPage,
    page,
    setPage,
    onSort,
    orderBy,
    orderDesc,
    selectedContacts,
    onChangeSelectedContacts,
    allContactsSelected,
    onToggleAllContactsSelected,
    selectedGroup,
    selectedPseudoGroup,
    filters,
    setFilters,
    onShowFilters,
    onSearch,
    onOpenContact,
    status,
    setStatus,
    tab,
    groups,
    selectedSegment,
    setSelectedSegment,
    allContactsCount,
    onExport,
    onCopy,
    onMove,
    onDeleteFromGroup,
    onDeleteFromMultipleGroups,
    onDeleteFromAccount,
    onSuppress,
    onUnsuppress,
    onUnsubscribe,
    onWarnNoSelection,
    onResubscribe,
    searchValue,
    onCreateGroup // TODO replace with hooks
}) => {
    const apiRef = useGridApiRef();
    const baseTheme = useTheme();
    const [views, setViews] = useState({});
    const [activeViewId, setActiveViewId] = useState(null);
    const [visibleColumns, setVisibleColumns] = useState(null);
    const [density, setDensity] = useState('compact');

    // this theme is created to overcome a bug where pinned columns take on the default background colour

    const theme = createTheme({
        ...baseTheme,
        palette: {
            background: {
                default: '#fff'
            }
        }
    });

    // note this minimum default value was added to prevent useResizeContainer from throwing an error in React 18
    const [tableHeight, setTableHeight] = useState(getTableHeight(theme));

    const calculateTableHeight = () => {
        setTableHeight(getTableHeight(theme));
    };

    const sort = orderDesc ? 'desc' : 'asc';

    const initialGridState = {
        sorting: {
            sortModel: [{ field: orderBy, sort }]
        },
        pinnedColumns: {
            left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'emailAddress']
        },
        columns: { columnVisibilityModel: {}, dimensions: {}, orderedFields: {} }, //MUI quirk: if this model isn't provided, column visibility isn't saved
        density: 'compact'
    };

    const [contactsViews, setContactsViews] = useLocalStorage('contactsViews', views);

    //disable string operator 'isAnyOf', which we currently don't support
    const dataColumns = useMemo(
        () =>
            columns.map(col => {
                if (col.type !== 'string') {
                    return col;
                }

                return {
                    ...col,
                    filterOperators: getGridStringOperators().filter(
                        operator => operator.value === 'contains'
                            || operator.value === 'equals'
                            || operator.value === 'startsWith'
                            || operator.value === 'endsWith'
                            || operator.value === 'isEmpty'
                            || operator.value === 'isNotEmpty'
                    )
                };
            }),
        [columns]
    );

    const handleOpenContact = params => {
        params?.id && onOpenContact(params.id);
    };

    const handleApplyColumns = columns => {
        const convertArrayToObject = (array, key) => {
            const initialValue = {};
            return array.reduce((obj, item) => {
                return {
                    ...obj,
                    [item[key]]: item.visible
                };
            }, initialValue);
        };

        const columnVisibilityModel = convertArrayToObject(columns, 'field');

        //adds checkboxes (visible true). Bit of a workaround, but we don't want users hiding checkboxes
        columnVisibilityModel['__check__'] = true;

        const currentState = apiRef.current.exportState();

        currentState.columns.columnVisibilityModel = columnVisibilityModel;

        apiRef.current.restoreState(currentState);
    };

    const handleOpenColumnPicker = () => {
        const currentState = apiRef.current.exportState();

        let columnsArray = columns.map(c => ({ title: c.headerName, field: c.field, visible: true }));

        if (currentState?.columns.columnVisibilityModel && Object.keys(currentState.columns.columnVisibilityModel).length > 0) {

            const cvmColumns = Object.entries(currentState.columns.columnVisibilityModel).map(([columnName, visible]) => ({ field: columnName, visible }));

            //match fieldnames and reassign
            const updatedColumns = columnsArray.map(col => {
                const matchedColumn = cvmColumns.find(e => e.field === col.field);

                if (matchedColumn) {
                    return { ...col, visible: matchedColumn.visible };
                }

                return col;
            });

            columnsArray = updatedColumns;
        }
        //if no columnVisibilityModel we can assume that all the columns are visible

        setVisibleColumns(columnsArray);
    };

    const handleChangeRowSelection = (model) => {
        if (Array.isArray(model)) {
            onChangeSelectedContacts({
                type: 'include',
                ids: new Set(model)
            });
        }
        else {
            onChangeSelectedContacts(model);
        }
    };

    useEffect(() => {
        setContactsViews(views);
    }, [views]);

    useEffect(() => {
        setViews(contactsViews);
    }, []);

    useEffect(() => {
        if (allContactsSelected && data?.length) {
            onChangeSelectedContacts({
                type: 'exclude',
                ids: new Set()
            });
        }
    }, [allContactsSelected, data]);

    useEffect(() => {
        calculateTableHeight();

        window.addEventListener('resize', calculateTableHeight);

        return () => window.removeEventListener('resize', calculateTableHeight);
    }, []);

    useEffect(() => {
        searchValue?.length === 0 && apiRef.current.setQuickFilterValues([]);
    }, [searchValue]);

    //todo?: move into a reset function and provide a button on Groups tab too... except this would need to trigger the sort reset in the reducer up a level. Probably have to send up a flag.
    //MUI open issue to make a reset state func: https://github.com/mui/mui-x/issues/4301
    //Until available, this:
    useEffect(() => {
        if (tab === 1) {
            setActiveViewId(null);

            const sortModel = [{ field: 'emailAddress', sort: 'asc' }];

            const convertArrayToObject = (array, key) => {
                const initialValue = {};
                return array.reduce((obj, item) => {
                    return {
                        ...obj,
                        [item[key]]: { width: item.width || 100 }
                    };
                }, initialValue);
            };

            const dimensions = convertArrayToObject(columns, 'field');

            //restoreState does a MERGE with the current state
            const resetState = {
                ...initialGridState,
                sorting: { sortModel },
                columns: {
                    ...initialGridState.columns,
                    dimensions
                }
            };

            apiRef.current.restoreState(resetState);
        }
    }, [tab]);

    return (
        <ThemeProvider theme={theme}>
            <div style={{ height: tableHeight, width: '100%' }}>
                <StyledDataGrid
                    apiRef={apiRef}
                    checkboxSelection={tab === 0}
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    disableColumnFilter
                    disableColumnSelector
                    disableMultipleColumnsSorting
                    pagination
                    keepNonExistentRowsSelected
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    onFilterModelChange={filters => onSearch(filters?.quickFilterValues)}
                    rows={data}
                    rowCount={count}
                    columns={dataColumns}
                    pageSizeOptions={rowsPerPageOptions}
                    paginationModel={{ page, pageSize: rowsPerPage }}
                    onPaginationModelChange={(paginationModel) => {
                        setRowsPerPage(paginationModel.pageSize);
                        setPage(paginationModel.page);
                    }}
                    onSortModelChange={props => onSort(props)}
                    sortingOrder={['desc', 'asc']}
                    rowSelectionModel={selectedContacts}
                    onRowSelectionModelChange={newRowSelectionModel => handleChangeRowSelection(newRowSelectionModel)}
                    isRowSelectable={() => allContactsSelected === false}
                    getRowClassName={params => params.row.isSuppressed ? 'row-suppressed' : null}
                    onRowClick={(params) => handleOpenContact(params)}
                    showToolbar={true}
                    density={density}
                    slots={{
                        toolbar: () => (
                            <DataGridTools
                                count={count}
                                allContactsSelected={allContactsSelected}
                                onToggleAllContactsSelected={onToggleAllContactsSelected}
                                selectedContacts={selectedContacts}
                                onShowFilters={onShowFilters}
                                filters={filters}
                                setFilters={setFilters}
                                status={status}
                                setStatus={setStatus}
                                tab={tab}
                                groups={groups}
                                selectedSegment={selectedSegment}
                                setSelectedSegment={setSelectedSegment}
                                selectedGroup={selectedGroup}
                                selectedPseudoGroup={selectedPseudoGroup}
                                allContactsCount={allContactsCount}
                                onExport={onExport}
                                onCopy={onCopy}
                                onMove={onMove}
                                onDeleteFromGroup={onDeleteFromGroup}
                                onDeleteFromMultipleGroups={onDeleteFromMultipleGroups}
                                onDeleteFromAccount={onDeleteFromAccount}
                                onSuppress={onSuppress}
                                onUnsuppress={onUnsuppress}
                                onUnsubscribe={onUnsubscribe}
                                views={views}
                                setViews={setViews}
                                activeViewId={activeViewId}
                                setActiveViewId={setActiveViewId}
                                onWarnNoSelection={onWarnNoSelection}
                                onResubscribe={onResubscribe}
                                setShowChooseColumnsDialog={handleOpenColumnPicker}
                                onCreateGroup={onCreateGroup} // TODO replace with hooks
                                density={density}
                                onDensityChange={(newDensity) => setDensity(newDensity)}
                            />
                        )
                    }}
                    initialState={initialGridState}
                />
                {visibleColumns && (
                    <ChooseColumnsDialog
                        open={Boolean(visibleColumns)}
                        onClose={() => setVisibleColumns(null)}
                        onSubmit={handleApplyColumns}
                        columns={visibleColumns}
                    />
                )}
            </div>
        </ThemeProvider>
    );
};

export default ContactsTable;